import React, { useEffect, useState, useCallback } from 'react';
import Joyride, { CallBackProps, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { useTheme } from '@mui/material';
import { useTour } from '../../contexts/TourContext';
import { useTourNavigation } from '../../hooks/useTourNavigation';
import { TourStep } from './TourStep';
import { tourSteps } from './tourSteps';
import toast from 'react-hot-toast';

export const ProductTour: React.FC = () => {
  const theme = useTheme();
  const { isTourActive, currentStep, setCurrentStep, completeTour, skipTour, stopTour } = useTour();
  const { navigateToStep } = useTourNavigation();
  const [run, setRun] = useState(false);

  // Start/stop tour based on context state
  // Wait for target element to exist before starting
  useEffect(() => {
    if (isTourActive) {
      // Check if the first tour step target exists
      const firstStepTarget = tourSteps[currentStep]?.target;
      if (firstStepTarget) {
        const checkElement = () => {
          const element = document.querySelector(firstStepTarget as string);
          if (element) {
            console.log('[Tour] Target element found, starting tour');
            setRun(true);
          } else {
            console.log('[Tour] Target element not found yet, retrying...');
            // Retry after a short delay
            setTimeout(checkElement, 100);
          }
        };
        checkElement();
      } else {
        setRun(true);
      }
    } else {
      setRun(false);
    }
  }, [isTourActive, currentStep]);

  // Handle tour callbacks (next, back, skip, finish)
  const handleJoyrideCallback = useCallback(
    async (data: CallBackProps) => {
      const { status, type, index, action } = data;

      console.log('[Tour] Callback:', { status, type, index, action });

      // Handle tour completion
      if (status === STATUS.FINISHED) {
        await completeTour();
        toast.success('Tour completed! You are all set to start using OnSite.');
        return;
      }

      // Handle tour skipped
      if (status === STATUS.SKIPPED) {
        await skipTour();
        toast('Tour skipped. You can restart it anytime from the navigation menu.');
        return;
      }

      // Handle errors (element not found, etc.)
      if (status === STATUS.ERROR) {
        console.error('[Tour] Error in tour step:', data);
        toast.error('There was an issue with the tour. Skipping to next step...');
        // Try to continue to next step
        const nextStepIndex = index + 1;
        if (nextStepIndex < tourSteps.length) {
          setCurrentStep(nextStepIndex);
        } else {
          stopTour();
        }
        return;
      }

      // Handle step changes
      if (type === EVENTS.STEP_AFTER) {
        const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);

        // Check if we need to navigate to a different page
        const currentStepConfig = tourSteps[index];
        const nextStepConfig = tourSteps[nextStepIndex];

        if (nextStepConfig && currentStepConfig.route !== nextStepConfig.route) {
          // Pause tour, navigate, then resume
          setRun(false);

          try {
            await navigateToStep(nextStepConfig);
            setCurrentStep(nextStepIndex);
            // Add a small delay before resuming to ensure joyride is ready
            setTimeout(() => setRun(true), 100);
          } catch (error) {
            console.error('Navigation error during tour:', error);
            toast.error('Failed to navigate to next step. Ending tour.');
            stopTour();
          }
        } else {
          setCurrentStep(nextStepIndex);
        }
      }
    },
    [completeTour, skipTour, stopTour, navigateToStep, setCurrentStep]
  );

  // Joyride styles matching MUI theme
  const joyrideStyles = {
    options: {
      overlayColor: 'rgba(0, 0, 0, 0.5)',
      primaryColor: theme.palette.primary.main,
      spotlightShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
      zIndex: 10000,
    },
    spotlight: {
      borderRadius: 8,
    },
  };

  return (
    <Joyride
      steps={tourSteps}
      run={run}
      stepIndex={currentStep}
      continuous
      showProgress
      showSkipButton
      callback={handleJoyrideCallback}
      styles={joyrideStyles}
      tooltipComponent={(props) => (
        <TourStep {...props} stepNumber={tourSteps[props.index]?.stepNumber} />
      )}
      disableScrolling={false}
      scrollToFirstStep
      scrollOffset={100}
      spotlightClicks={false}
      disableOverlayClose
      hideCloseButton
      disableCloseOnEsc={false}
    />
  );
};
